import React, { useMemo, useState } from "react";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import deviceStore from "../../stores/DeviceStore";
import logStore from "../../stores/LogStore";
import CustomDataGridPro from "../CustomDataGridPro";

const DeviceListPending = observer(({ devices = [] }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortModel, setSortModel] = useState([{ field: "id", sort: "asc" }]);

  const rows = useMemo(() => {
    return devices.map((device) => ({
      id: device.id,
      name: device.name || "N/A",
      manufacture: device.manufacture,
      type_name: device.type_name,
      status:
        logStore.deviceStatusMap?.get?.(device.pimios_id) || "Not Connected",
      location: device.location
        ? `${device.location.street}, ${device.location.city}`
        : "N/A",
      region: device.region_name || "Unknown",
      missingFields: device.missingFields || [],
      raw: device,
    }));
  }, [devices]);

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 1,
      renderCell: ({ row }) => row.id,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 2,
      renderCell: ({ row }) => row.name,
    },
    {
      field: "manufacture",
      headerName: "Manufacture - Type",
      flex: 3,
      renderCell: ({ row }) => `${row.manufacture} - ${row.type_name}`,
    },
    {
      field: "region",
      headerName: "Region ",
      flex: 2,
      renderCell: ({ row }) => row.region,
    },
    {
      field: "location",
      headerName: "Location",
      flex: 2,
      renderCell: ({ row }) => row.location,
    },
    // ✅ Live status chip derived from logStore.connectedDeviceIds
    {
      field: "status",
      headerName: "Status",
      flex: 2,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: ({ row }) => {
        const pimiosId = row.raw?.pimios_id;
        const isConnected = pimiosId
          ? logStore.connectedDeviceIds.has(pimiosId)
          : false;

        const config = {
          label: isConnected ? "Connected" : "Not Connected",
          icon: isConnected ? (
            <CheckCircleOutlinedIcon sx={{ color: "#fff", fontSize: "1rem" }} />
          ) : (
            <CancelOutlinedIcon sx={{ color: "#fff", fontSize: "1rem" }} />
          ),
          bgColor: isConnected ? "#16a34a" : "#dc2626",
          fontWeight: 700,
          textColor: "#fff",
        };

        return (
          <Chip
            icon={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pl: "2px",
                }}
              >
                {config.icon}
              </Box>
            }
            label={
              <Typography
                sx={{
                  fontWeight: config.fontWeight,
                  fontSize: "0.8rem",
                  textTransform: "none",
                }}
              >
                {config.label}
              </Typography>
            }
            sx={{
              minWidth: 150,
              px: 1.5,
              py: 0.5,
              height: "auto",
              borderRadius: "999px",
              backgroundColor: config.bgColor,
              color: config.textColor,
              textTransform: "none",
            }}
          />
        );
      },
    },
    {
      field: "missing-fields",
      headerName: "Missing Fields",
      flex: 3,
      renderCell: ({ row }) =>
        row.missingFields.length > 0 ? (
          row.missingFields.map((field) => (
            <Chip
              key={field}
              label={`Missing ${field}`}
              color="warning"
              size="small"
              sx={{ mr: 0.5 }}
            />
          ))
        ) : (
          <Chip label="None" color="success" size="small" />
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.5,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box>
          <IconButton
            sx={{
              color: "#22C55E",
              mr: 1,
              "&:hover": { backgroundColor: "#BBF7D0" },
            }}
            onClick={() => navigate(`/edit-unit/${row.raw.id}`)}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            sx={{
              backgroundColor: "#F3F4F6",
              color: "#6B7280",
              "&:hover": { backgroundColor: "#E5E7EB" },
            }}
            onClick={() => deviceStore.deleteDevice(row.raw.id)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <CustomDataGridPro
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      page={page}
      setPage={setPage}
      pageSize={pageSize}
      setPageSize={setPageSize}
      totalCount={rows.length}
      sortModel={sortModel}
      onSortModelChange={(model) => setSortModel(model)}
      sortingMode="client"
    />
  );
});

DeviceListPending.propTypes = {
  devices: PropTypes.array.isRequired,
};

export default DeviceListPending;
